import { FC, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { StyledPageWrapper, Title } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { arrayBufferToBase64 } from "@monorepo/helpers";
import { historyApi, useGetHistoryByOrderIdQuery } from "@monorepo/services/api/history";
import { setPayment } from "@monorepo/slices/payments";
import theme from "@monorepo/styles/theme";
import { IAttributesSliceState, IDetailedHistoryEntry, IOrderData, ServiceParameterEnum } from "@monorepo/types";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Typography } from "@mui/material";
import { reverse } from "named-urls";

const ActionButtons = ({ historyEntry, orderId }: { historyEntry: IDetailedHistoryEntry; orderId: number }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const attributesObject = useSelector(
    ({ attributesSlice }: IAttributesSliceState) => attributesSlice?.["attributes-object"]
  );
  const { isFetching } = historyApi.endpoints.getHistoryReportById.useQueryState(orderId);

  const handleFileDownload = useCallback(async () => {
    const subscription = dispatch(historyApi.endpoints.getHistoryReportById.initiate(orderId));
    //todo dispatch type
    //@ts-ignore
    const { data } = await subscription;

    if (data) {
      const fileName = t("payment_history.order_statement") + " - " + String(orderId) + ".pdf";
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(data, fileName);
      }
      const blob = new Blob([data], { type: "application/pdf" });
      const fileUrl = URL.createObjectURL(blob);
      if (attributesObject?.isPdfNeededForApp) {
        window.parent.postMessage(
          {
            data: {
              data: arrayBufferToBase64(data),
              fileName,
            },
            eventType: "pdf-download",
            type: "third-party",
          },
          attributesObject.parentOriginUrl
        );
      } else if (navigator.userAgent.includes("Chrome") || navigator.userAgent.includes("Firefox")) {
        const w = window.open(fileUrl, "_blank");
        w?.focus();
      } else {
        window.location.href = fileUrl;
      }
    }
    //todo dispatch type
    //@ts-ignore
    subscription.unsubscribe();
  }, [orderId, dispatch, t, attributesObject]);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", margin: "0 auto" }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            margin: `${theme.spacing(3)} 0`,
          }}
        >
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              history.push(
                reverse(routeConfig.dashboard.payments.history.cartGroup.order.service, {
                  orderId,
                  serviceId: historyEntry.service_id,
                  cartGroupId: historyEntry.cart_group_id,
                })
              );
            }}
          >
            {t("payment_history.view_history")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            sx={{
              background: theme.palette.secondary.main,
              marginLeft: theme.spacing(1),
            }}
            disableElevation
            onClick={() => {
              dispatch(
                setPayment({
                  serviceId: historyEntry.service_id,
                  serviceParameterValue: Array(historyEntry.customer_number[0].parameter_value),
                  serviceParameterId: Array(historyEntry.customer_number[0].parameter_name),
                  cartGroupId: String(historyEntry.cart_group_id),
                  parameters: historyEntry.data,
                })
              );
              history.push(
                reverse(routeConfig.dashboard.payments.repeatPayment.serviceId, {
                  serviceId: historyEntry.service_id,
                })
              );
            }}
          >
            {t("payment_history.repeat_payment")}
          </Button>
        </Box>
        <Box position="relative" sx={{ minHeight: "2rem" }}>
          <LoadingButton
            onClick={handleFileDownload}
            sx={{ color: theme.palette.text.disabled }}
            variant="text"
            disabled={isFetching}
            loading={isFetching}
            startIcon={<DescriptionOutlinedIcon sx={{ marginRight: "8px" }} />}
          >
            {t("payment_history.download_order_statement")}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

const HistoryField: FC<{ fieldName: string; value: string }> = memo(({ value, fieldName }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      flex="1"
      sx={{
        borderBottom: `1px solid ${theme.palette.geyserGray.main}`,
        pb: theme.spacing(4 / 8),
        mt: theme.spacing(2),
      }}
    >
      <Typography
        color={theme.palette.text.disabled}
        sx={{
          textTransform: "capitalize",
        }}
        fontSize="0.825rem"
      >
        {fieldName.toLowerCase()}
      </Typography>
      <Typography sx={{ mt: theme.spacing(4 / 8) }}>{value}</Typography>
    </Box>
  );
});
HistoryField.displayName = "HistoryField";

const HistoryOrder: FC<{ orderId: number; cartGroupId: number }> = ({ orderId }) => {
  const { t } = useTranslation();
  const { data: historyEntry } = useGetHistoryByOrderIdQuery(orderId);

  if (!historyEntry) return null;

  const isMeterDataEntry = (data: IOrderData) => ["from", "to"].includes(String(data.meter_type));

  const getFieldColumn = (data: IOrderData) =>
    isMeterDataEntry(data) ||
    [ServiceParameterEnum.SKOLA_PERMOKA, ServiceParameterEnum.AMT].includes(data.parameter_name)
      ? 2
      : 1;

  const metersData: Record<string, Array<IOrderData>> = historyEntry.data
    .filter((e) => isMeterDataEntry(e) && !e.hidden)
    .reduce((acc: typeof metersData, cur) => {
      if (cur.group_label) {
        // eslint-disable-next-line no-param-reassign
        if (!acc[cur.group_label]) acc[cur.group_label] = [];
        acc[cur.group_label].push(cur);
      }

      return acc;
    }, {});

  return (
    <StyledPageWrapper
      noJustify
      sx={{
        pb: {
          md: 0,
        },
      }}
    >
      <Title>{t("payment_history.label_single")}</Title>
      <Typography textAlign="center" variant="h6">
        {historyEntry.service_name}
      </Typography>
      <Box display="flex" width="100%" flexDirection={{ xs: "column", md: "row" }}>
        <Box display="flex" flexDirection="column" flex="1" height="fit-content">
          {historyEntry.data
            .filter((data) => !data.hidden && getFieldColumn(data) === 1)
            .map((data, key) => (
              // eslint-disable-next-line react/no-array-index-key
              <HistoryField key={key} value={data.value} fieldName={data.api_parameter_label} />
            ))}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          sx={{
            ml: {
              xs: 0,
              md: theme.spacing(4),
            },
          }}
          flex="1"
          height="fit-content"
        >
          {Object.entries(metersData || {}).map(([groupLabel, properties]) => (
            <Box mt={theme.spacing(2)}>
              <Typography fontWeight={600} fontSize="0.825rem" color={theme.palette.text.primary}>
                {groupLabel}
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" sx={{ gridGap: theme.spacing(2) }}>
                {properties.map((prop) => (
                  <HistoryField value={prop.value} fieldName={prop.api_parameter_label} />
                ))}
              </Box>
            </Box>
          ))}

          {historyEntry.data
            .filter((data) => !data.hidden && getFieldColumn(data) === 2 && !isMeterDataEntry(data))
            .map((data, key) => (
              // eslint-disable-next-line react/no-array-index-key
              <HistoryField key={key} value={data.value} fieldName={data.api_parameter_label} />
            ))}
        </Box>
      </Box>
      <ActionButtons orderId={orderId} historyEntry={historyEntry} />
    </StyledPageWrapper>
  );
};
export default HistoryOrder;
